import React, { useEffect, useMemo } from 'react';
import { useFormContext, useFieldArray, useWatch } from 'react-hook-form';
import CgButton from 'modules/Common/ui/CgButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as faIcons from '@fortawesome/free-solid-svg-icons';
import { FormField, SelectField, TextField, Label } from 'modules/Common/form/v2';
import { useTranslation } from 'react-i18next';
import { resolveValidationMessage } from 'helpers/validationMessages';
import { anchorProps } from 'modules/Common/utils/gapAnchor';
import { recordFieldAnchor } from 'modules/Common/utils/recordLanding';

const buildReverseMapping = (t) => ({
  '01': t('installations:production.valueTypeNumber', 'Number'),
  '02': t('installations:production.valueTypePercentage', 'Percentage'),
  '03': t('installations:production.valueTypeString', 'String'),
  '04': t('installations:production.valueTypeRange', 'Range'),
  '05': t('installations:production.valueTypeRangeOfPercentage', 'Range of percentage'),
});

const typeMapping = {
  NUMBER: '01',
  PERCENTAGE: '02',
  STRING: '03',
  RANGE: '04',
};

// Sub-component for each qualifying parameter row. Needs its own component
// so that useWatch hook calls are stable (cannot call hooks inside .map).
function DirectParameterRow({
  methodIndex,
  paramIndex,
  control,
  categoryMethod,
  onRemove,
  lockForm,
}) {
  const { t } = useTranslation('installations');
  const reverseMapping = useMemo(() => buildReverseMapping(t), [t]);
  const { setValue } = useFormContext();
  const paramId = useWatch({
    control,
    name: `productionMethods.${methodIndex}.directParameters.${paramIndex}.id`,
  });
  const valueType = useWatch({
    control,
    name: `productionMethods.${methodIndex}.directParameters.${paramIndex}.valueType`,
  });

  const handleParamIdChange = (selectedId) => {
    const selected = categoryMethod?.qualifyingParameters?.find(
      (p) => p.parameterid === selectedId,
    );
    if (!selected) return;
    const base = `productionMethods.${methodIndex}.directParameters.${paramIndex}`;
    const mappedType = Object.values(typeMapping).includes(selected.valueType)
      ? selected.valueType
      : (typeMapping[selected.valueType] ?? selected.valueType);
    setValue(`${base}.id`, selected.parameterid);
    setValue(`${base}.name`, selected.parametername);
    setValue(`${base}.valueType`, mappedType);
    setValue(
      `${base}.value`,
      selected.valueType === 'STRING' ? (selected.enumValues?.[0] ?? '') : '100',
    );
    setValue(`${base}.description`, '');
  };

  const enumValues = useMemo(() => {
    if (!categoryMethod || !paramId) return [];
    const param = categoryMethod.qualifyingParameters?.find((p) => p.parameterid === paramId);
    return param?.enumValues ?? [];
  }, [categoryMethod, paramId]);

  return (
    <div className="grid grid-cols-12 gap-3 mb-2 items-start">
      <div className="col-span-12">
        <div className="flex items-center gap-2 mb-2">
          <h3 style={{ fontSize: '0.95rem', marginBottom: 0 }}>
            {t('production.directQualifyingParam', 'Direct Qualifying Parameter {{n}}', {
              n: paramIndex + 1,
            })}
          </h3>
          {!lockForm && (
            <button
              type="button"
              className="p-0 border-0 bg-transparent text-red-500 hover:text-red-600"
              onClick={onRemove}
            >
              <FontAwesomeIcon icon={faIcons.faTrashCan} />
            </button>
          )}
        </div>
      </div>

      {/* Qualifying parameter selector — unregistered, populates sibling fields.
          Label markup mirrors FormField (mb-1 wrapper, mb-0 label) so this row
          lines up with the FormField-based siblings to its right. */}
      <div className="col-span-12 md:col-span-4">
        <div className="flex items-center gap-1.5 mb-1">
          <Label
            htmlFor={`qualParam-${methodIndex}-${paramIndex}`}
            className="mb-0"
          >
            {t('production.qualifyingParamLabel', 'Qualifying Parameter *')}
          </Label>
        </div>
        <SelectField
          field={{
            value: paramId ?? '',
            onChange: handleParamIdChange,
            onBlur: () => {},
            name: `qualParam-${methodIndex}-${paramIndex}`,
          }}
          id={`qualParam-${methodIndex}-${paramIndex}`}
          options={(categoryMethod?.qualifyingParameters ?? []).map((param) => ({
            value: param.parameterid,
            label: param.parametername,
          }))}
          placeholder={t('production.selectParam', 'Select a Parameter')}
          disabled={lockForm}
        />
      </div>

      {/* Value field — SelectField for STRING enum, TextField otherwise */}
      {valueType === '03' ? (
        <FormField
          name={`productionMethods.${methodIndex}.directParameters.${paramIndex}.value`}
          label={t('production.valueWithType', 'Value ({{type}})', {
            type: reverseMapping[valueType] ?? '',
          })}
          colSpan={4}
        >
          <SelectField
            options={enumValues.map((v) => ({ value: v, label: v }))}
            disabled={lockForm}
          />
        </FormField>
      ) : (
        <FormField
          name={`productionMethods.${methodIndex}.directParameters.${paramIndex}.value`}
          label={
            valueType
              ? t('production.valueWithTypeRequired', 'Value ({{type}}) *', {
                  type: reverseMapping[valueType] ?? '',
                })
              : t('production.valueRequired', 'Value *')
          }
          colSpan={4}
        >
          <TextField disabled={!paramId || lockForm} />
        </FormField>
      )}

      {/* Parameter ID — read-only */}
      <FormField
        name={`productionMethods.${methodIndex}.directParameters.${paramIndex}.id`}
        label={t('production.paramIdReq', 'Parameter ID *')}
        colSpan={4}
      >
        <TextField disabled={true} />
      </FormField>

      {/* Description */}
      <FormField
        name={`productionMethods.${methodIndex}.directParameters.${paramIndex}.description`}
        label={t('section.additionalInfo', 'Additional Information')}
        colSpan={12}
      >
        <TextField
          textarea
          rows={2}
          disabled={lockForm}
        />
      </FormField>
    </div>
  );
}

// Sub-component for each production method. Each needs its own useFieldArray
// for directParameters — hooks cannot be called inside .map() in the parent.
function ProductionMethodRow({
  methodIndex,
  control,
  categoryProductionMethods,
  onRemove,
  lockForm,
  loading,
}) {
  const { t } = useTranslation('installations');
  const { setValue } = useFormContext();
  const {
    fields: paramFields,
    append: appendParam,
    remove: removeParam,
  } = useFieldArray({
    control,
    name: `productionMethods.${methodIndex}.directParameters`,
  });

  const methodCode = useWatch({
    control,
    name: `productionMethods.${methodIndex}.methodCode`,
  });

  const categoryMethod = useMemo(
    () => categoryProductionMethods?.find((p) => p.id === methodCode),
    [categoryProductionMethods, methodCode],
  );

  // Keep the sibling `name` field in lockstep with `methodCode`. SelectField
  // only exposes `field.onChange`, so the value→label sync we used to do
  // inside react-select's onChange now lives here. Skip when methodCode is
  // set but the methods list hasn't loaded yet — otherwise we'd clobber the
  // name loaded from defaultValues on mount.
  useEffect(() => {
    if (!methodCode) {
      setValue(`productionMethods.${methodIndex}.name`, '');
      return;
    }
    if (categoryMethod) {
      setValue(`productionMethods.${methodIndex}.name`, categoryMethod.name);
    }
  }, [methodCode, categoryMethod, methodIndex, setValue]);

  const canAddParam = paramFields.length < (categoryMethod?.qualifyingParameters?.length ?? 0);

  const methodOptions = useMemo(
    () =>
      (categoryProductionMethods || []).map((p) => ({
        value: p.id,
        label: `${p.name} - ${p.id}`,
      })),
    [categoryProductionMethods],
  );

  return (
    <div
      className="p-4"
      style={{
        backgroundColor: methodIndex % 2 === 0 ? 'white' : '#F2F2F2',
        marginLeft: '-1rem',
        marginRight: '-1rem',
      }}
    >
      <div className="flex items-center gap-2 mb-4">
        <h2 style={{ fontSize: '1rem', marginBottom: 0 }}>
          {methodIndex === 0
            ? t('production.methodHeading', 'Production Method')
            : t('production.methodHeadingN', 'Production Method {{n}}', { n: methodIndex + 1 })}
        </h2>
        {!lockForm && (
          <button
            type="button"
            className="p-0 border-0 bg-transparent text-red-500 hover:text-red-600"
            onClick={onRemove}
          >
            <FontAwesomeIcon icon={faIcons.faTrashCan} />
          </button>
        )}
      </div>

      <div className="grid grid-cols-12 gap-3 mb-3">
        <FormField
          name={`productionMethods.${methodIndex}.methodCode`}
          label={t('production.methodName', 'Production Method Name')}
          colSpan={6}
          anchor={methodIndex === 0 ? recordFieldAnchor('productionMethods') : null}
        >
          <SelectField
            options={methodOptions}
            placeholder={t('production.selectMethod', 'Select a production method...')}
            disabled={lockForm}
          />
        </FormField>
      </div>

      {/* Direct Parameters */}
      <div>
        {paramFields.map((paramField, paramIndex) => (
          <DirectParameterRow
            key={paramField.id}
            methodIndex={methodIndex}
            paramIndex={paramIndex}
            control={control}
            categoryMethod={categoryMethod}
            onRemove={() => removeParam(paramIndex)}
            lockForm={lockForm}
          />
        ))}

        {canAddParam && !lockForm && (
          <CgButton
            variant="primary"
            type="button"
            size="sm"
            disabled={loading}
            onClick={() =>
              appendParam({ id: '', name: '', valueType: '', value: '', description: '' })
            }
            icon="faPlus"
            label={t('production.addDirectParam', 'Add Direct Parameter')}
          />
        )}
      </div>
    </div>
  );
}

export function ProductionMethodsTab({ categoryProductionMethods, loading, lockForm }) {
  const { t } = useTranslation('installations');
  const {
    control,
    formState: { errors },
  } = useFormContext();

  const {
    fields: methodFields,
    append,
    remove,
  } = useFieldArray({
    control,
    name: 'productionMethods',
  });

  return (
    <div>
      {errors.productionMethods?.message && (
        <p className="text-red-500 text-sm mb-2">
          {resolveValidationMessage(errors.productionMethods.message)}
        </p>
      )}
      {errors.productionMethods?.root?.message && (
        <p className="text-red-500 text-sm mb-2">
          {resolveValidationMessage(errors.productionMethods.root.message)}
        </p>
      )}

      {/* Without a resolved category there is nothing to offer, and the Add
          button below is gated on that same list — which previously left this
          tab blank next to a "required" error, with no way to satisfy it. Say
          so instead of rendering nothing. */}
      {(categoryProductionMethods?.length ?? 0) === 0 && (
        <p className="text-slate-500 text-sm">
          No production methods are defined for this good&apos;s category, so none can be recorded
          here.
        </p>
      )}

      {methodFields.map((methodField, index) => (
        <ProductionMethodRow
          key={methodField.id}
          methodIndex={index}
          control={control}
          categoryProductionMethods={categoryProductionMethods}
          onRemove={() => remove(index)}
          lockForm={lockForm}
          loading={loading}
        />
      ))}

      {!lockForm && methodFields.length < (categoryProductionMethods?.length ?? 0) && (
        <div className="flex justify-center mt-3">
          <span
            className="inline-flex"
            {...(methodFields.length === 0 ? anchorProps('recordField', 'productionMethods') : {})}
          >
            <CgButton
              variant="primary"
              type="button"
              disabled={loading || lockForm}
              onClick={() =>
                append({ methodCode: '', name: '', steelMill: '', directParameters: [] })
              }
              icon="faPlus"
              label={t('production.addMethod', 'Add Production Method')}
            />
          </span>
        </div>
      )}
    </div>
  );
}
